import { useQuery } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";

import {
  decodeJwtPayload,
  evaluateAdminMfa,
  sessionIsAal2,
  type AdminMfaOutcome,
} from "../domain/auth/adminMfaPolicy";
import supabase from "../lib/supabase";
import type { UserRole } from "../types/domain";

/**
 * must-19 — bepaalt of deze beheerder door mag zonder tweede factor.
 *
 * Leest drie feiten en combineert ze met de gedeelde beleidsfunctie:
 *  - de rol en de deadline uit `users` (RLS: eigen rij);
 *  - of er een GEVERIFIEERDE factor bestaat (Supabase Auth);
 *  - of de huidige sessie op aal2 zit (uit de tokenpayload, niet uit een vlag).
 */
/**
 * Gedeelde sleutel. Bestaat omdat de uitkomst 30 seconden vers blijft: wie de
 * tweede factor zojuist heeft doorlopen MOET deze query kunnen invalideren,
 * anders beslist de poort op een antwoord van vóór de verificatie. Precies dat
 * gebeurde op productie (05-08-2026) en leverde een uitsluiting op.
 */
export const ADMIN_MFA_STATUS_KEY = "admin-mfa-status";

export function useAdminMfaStatus(session: Session | null): {
  outcome: AdminMfaOutcome | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const userId = session?.user.id;
  const payload = decodeJwtPayload(session?.access_token);
  // Een oordeel over aal2 geldt nooit voor een nieuwe aal1-sessie van dezelfde
  // gebruiker. Een gewone refresh behoudt sessie-id en assurance, dus geen flits.
  const sessionId = typeof payload?.session_id === "string" ? payload.session_id : null;
  const aal = typeof payload?.aal === "string" ? payload.aal : null;
  const query = useQuery({
    queryKey: [ADMIN_MFA_STATUS_KEY, userId, sessionId, aal],
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async ({ signal }): Promise<AdminMfaOutcome> => {
      /*
       * De deadline-beslissing komt van de SERVER: een vergelijking met de
       * browserklok zou manipuleerbaar zijn (systeemklok terugzetten = uitstel).
       */
      const requirement = await supabase.rpc("collectr_admin_mfa_requirement").abortSignal(signal);
      if (requirement.error) throw new Error(requirement.error.message);

      const row = (Array.isArray(requirement.data) ? requirement.data[0] : requirement.data) as
        | { role?: string | null; grace_expired?: boolean; seconds_left?: number | string }
        | null;

      const role = (row?.role ?? null) as UserRole | null;
      // Geen serverantwoord ⇒ behandelen als verstreken (fail-closed).
      const graceExpired = row?.grace_expired !== false;
      const secondsLeft = Number(row?.seconds_left ?? 0);

      const factors = await supabase.auth.mfa.listFactors();
      if (factors.error) throw new Error(factors.error.message);
      const hasVerifiedFactor = (factors.data?.totp ?? []).some((f) => f.status === "verified");

      // Het oordeel hoort bij de sessie in de query-sleutel, nooit bij een
      // inmiddels vervangen sessie die getSession() later zou teruggeven.
      signal.throwIfAborted();
      return evaluateAdminMfa({
        role,
        hasVerifiedFactor,
        sessionAtAal2: sessionIsAal2(payload),
        graceExpired,
        secondsLeft,
      });
    },
  });

  return {
    outcome: query.data ?? null,
    isLoading: Boolean(userId) && query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
